/**
 * Componente: Modal de Selección de Idioma — IdiomaModalSelector
 * Estilo unificado y limpio, idéntico a PaisModalSelector, EstadoModalSelector y CiudadModalSelector
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './IdiomaModalSelector.module.css';

export interface IdiomaItem {
  code: 'es' | 'en' | 'pt';
  label: string;
  flag: React.ReactNode;
}

export interface IdiomaModalSelectorProps {
  isOpen: boolean;
  currentCode: 'es' | 'en' | 'pt';
  idiomas: IdiomaItem[];
  onSelect: (item: IdiomaItem) => void;
  onClose: () => void;
}

export function IdiomaModalSelector({
  isOpen,
  currentCode,
  idiomas,
  onSelect,
  onClose,
}: IdiomaModalSelectorProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="idioma-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono Degradado y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <span className="material-symbols-outlined text-[24px]">translate</span>
          </div>
          <div>
            <h2
              id="idioma-modal-title"
              className="text-lg sm:text-xl font-bold text-gray-900 leading-tight"
            >
              Selecciona tu idioma
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Personaliza la plataforma a tu preferencia
            </p>
          </div>
        </div>

        {/* Lista Limpia de Idiomas */}
        <div className={styles.languageList}>
          {idiomas.map((item) => {
            const isSelected = item.code === currentCode;
            return (
              <button
                key={item.code}
                type="button"
                onClick={() => {
                  onSelect(item);
                  onClose();
                }}
                className={`${styles.languageButton} ${
                  isSelected ? styles.languageButtonSelected : ''
                }`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <span className={styles.flagBadge}>
                    {item.flag}
                  </span>
                  <span className="text-sm sm:text-base font-semibold truncate text-gray-800">
                    {item.label}
                  </span>
                </div>

                {isSelected ? (
                  <span className="material-symbols-outlined text-[#c25ee8] text-[22px] flex-shrink-0">
                    check_circle
                  </span>
                ) : (
                  <div className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
